import type { NitroFetchOptions, NitroFetchRequest } from "nitropack";

/** 后端统一响应结构 */
export interface ApiResponse<T = any> {
  resultCode: number;
  message: string;
  data: T;
}

/** GET 的参数类型 */
type GetParams = Record<string, any>;

/** 请求方法签名 */
type RequestMethod = <T = any>(
  url: string,
  params?: GetParams,
  options?: NitroFetchOptions<NitroFetchRequest>,
) => Promise<T>;

type BodyMethod = <T = any>(
  url: string,
  body: Record<string, any>,
  options?: NitroFetchOptions<NitroFetchRequest>,
) => Promise<T>;

type DeleteMethod = <T = any>(
  url: string,
  options?: NitroFetchOptions<NitroFetchRequest>,
) => Promise<T>;

export interface UseRequestReturn {
  $http: typeof $fetch;
  GET: RequestMethod;
  POST: BodyMethod;
  PUT: BodyMethod;
  DELETE: DeleteMethod;
}

export const useRequest = (): UseRequestReturn => {
  const config = useRuntimeConfig();

  const $http = $fetch.create({
    baseURL: config.public.baseURL,
    timeout: 300000,

    onRequest({}) {},

    onResponse({ response }) {
      const res = response._data as ApiResponse;
      if (res.resultCode == 0) {
        // 剥掉外层，业务层直接拿 data
        response._data = res.data;
      } else {
        throw createError({
          statusCode: res.resultCode,
          statusMessage: res.message,
        });
      }
    },

    onResponseError({ response }) {
      const res = response._data as ApiResponse;
      throw createError({
        statusCode: res.resultCode,
        statusMessage: res.message,
      });
    },
  });

  return {
    $http,

    GET: <T = any>(
      url: string,
      params?: GetParams,
      options?: NitroFetchOptions<NitroFetchRequest>,
    ): Promise<T> => $http<T>(url, { method: "GET", params, ...options }),

    POST: <T = any>(
      url: string,
      body: Record<string, any>,
      options?: NitroFetchOptions<NitroFetchRequest>,
    ): Promise<T> => $http<T>(url, { method: "POST", body, ...options }),

    PUT: <T = any>(
      url: string,
      body: Record<string, any>,
      options?: NitroFetchOptions<NitroFetchRequest>,
    ): Promise<T> => $http<T>(url, { method: "PUT", body, ...options }),

    DELETE: <T = any>(
      url: string,
      options?: NitroFetchOptions<NitroFetchRequest>,
    ): Promise<T> => $http<T>(url, { method: "DELETE", ...options }),
  };
};